import { time } from '@/shared/datetime/utils/time'

import type {
	AgentLogSourceVolumeDTO,
	AgentVolumeDTO,
	VolumeBreakdown,
	VolumeReading,
} from './types'

/**
 * How often a volume read refetches.
 *
 * Far slower than collector status, which refreshes every few seconds while
 * something is settling: a trailing-24h total barely moves between two reads,
 * and the fleet figure is an aggregation the backend should not run on the
 * status cadence.
 */
export const VOLUME_POLL_INTERVAL = time(5, 'm')

/** The caption every volume figure carries, so none of them reads as billing. */
export const VOLUME_CAPTION = 'Raw ingress, pre-compression'

/** `GET /agents/volume`, keyed by collector. */
export function toFleetVolume(dto: AgentVolumeDTO): VolumeReading {
	return {
		bytes: dto.bytes,
		breakdown: Object.fromEntries(
			dto.agents.map(({ agent_id, bytes, log_count }) => [
				agent_id,
				{ bytes, log_count },
			]),
		),
	}
}

/** `GET /agents/{agent_id}/volume`, keyed by log source. */
export function toCollectorVolume(dto: AgentLogSourceVolumeDTO): VolumeReading {
	return {
		bytes: dto.bytes,
		breakdown: Object.fromEntries(
			dto.log_sources.map(({ log_source_id, bytes, log_count }) => [
				log_source_id,
				{ bytes, log_count },
			]),
		),
	}
}

/**
 * One roster member's bytes in the window.
 *
 * Zero when the breakdown does not name it: the volume routes omit a subject
 * that sent nothing rather than listing it at zero. Rows always come from the
 * roster, and this is the only way they read a figure.
 */
export function volumeFor(breakdown: VolumeBreakdown, id: string): number {
	return Object.hasOwn(breakdown, id) ? breakdown[id].bytes : 0
}
